/* eslint-disable max-len */
import React, {
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as ShotsActions from '../../../stores/actions/shots';
import useShotMemos from '../../../hooks/use-shot-memos';
import * as Storage from '../../../utils/local-storage';
import Socket from '../../../connection/socket';
import useAutoplay from '../../../hooks/use-autoplay';
import type { Shot } from '../../../stores/types/shots';
import blpAuth from '../../../connection/blp-auth';
import { GreenroomState } from '../../../stores/types/types';
import { getSyncHost } from '../../../utils/hosts';

export enum DebugLocalStorage {
	PanelEnabled = '_debug-panel-enabled',
	OverlaysOn = '_debug-panel-overlays-on',
	EchoCancellationOn = '_debug-panel-echo-cancellation-on',
	SocketCanDispatch = '_debug-panel-socket-can-dispatch',
	DebugOpentok = '_debug-opentok',
	debugKillServer = '_debug-kill-server',
	debugDisconnectProducer = '_debug-disconnect-producer',
	debugLogIsoAlerts = '_debug-log-iso-alerts',
	avUI = '_debug-AV-UI',
}

interface LegacyChromeMemoryInfo {
	jsHeapSizeLimit: number;
	totalJSHeapSize: number;
	usedJSHeapSize: number;
}

interface LegacyChromeOnlyPerf extends Performance {
	memory: LegacyChromeMemoryInfo;
}

interface DebugPanelProps {
	socket: Socket | null;
}

export default function DebugSettings({ socket }: DebugPanelProps) {
	const dispatch = useDispatch();

	const roomId = useSelector((s: GreenroomState) => s.SessionReducer.meeting?.meeting)

	const { currentShot } = useShotMemos();

	const { getShotAutoplay } = useAutoplay();

	const [jsHeapSizeLimit, setJsHeapSizeLimit] = useState<number>(0);
	const [totalJSHeapSize, setTotalJSHeapSize] = useState<number>(0);
	const [usedJSHeapSize, setUsedJSHeapSize] = useState<number>(0);

	const [shouldSetRedux, setShouldSetRedux] = useState<boolean>(false);
	const [shouldSetSocket, setShouldSetSocket] = useState<boolean>(false);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [localLog, setLocalLog] = useState<string[]>([]);

	const [watchAutoplayTimers, setWatchAutoplayTimers] = useState<boolean>(false);
	const [autoplayTimersText, setAutoplayTimersText] = useState<string>('');

	const setReduxInterval = useRef<NodeJS.Timeout | null>(null);
	const setSocketInterval = useRef<NodeJS.Timeout | null>(null);
	const setAutoplayTimersInterval = useRef<NodeJS.Timeout | null>(null);

	const getRoundedMemory = useCallback(
		(memory: number): number => Math.round(memory / 1000 ** 2),
		[],
	);

	const resetLocalStorage = () => {
		Object.values(DebugLocalStorage).forEach((key) => Storage.removeStorageItem(key));
	};

	useEffect(() => {
		const perf = window.performance as LegacyChromeOnlyPerf;

		const memInterval = setInterval(() => {
			if (perf?.memory) {
				setJsHeapSizeLimit(getRoundedMemory(perf.memory.jsHeapSizeLimit));
				setTotalJSHeapSize(getRoundedMemory(perf.memory.totalJSHeapSize));
				setUsedJSHeapSize(getRoundedMemory(perf.memory.usedJSHeapSize));
			}
		}, 10000);

		return () => {
			clearInterval(memInterval);
		};
	}, []);

	useEffect(() => {
		if (shouldSetRedux) {
			setReduxInterval.current = setInterval(() => {
				for (let i = 0; i < 10; i += 1) {
					localLog.push('setShotModalShot');
					dispatch(ShotsActions.setShotModalShot({ ...currentShot }));
				}
			}, 100);
		} else if (setReduxInterval.current) {
			clearInterval(setReduxInterval.current);
		}
	}, [shouldSetRedux]);

	useEffect(() => {
		if (shouldSetSocket) {
			setSocketInterval.current = setInterval(() => {
				localLog.push('trigger-load-shots');
				socket?.sendMessage('trigger-load-shots', {});
			}, 0);
		} else if (setSocketInterval.current) {
			clearInterval(setSocketInterval.current);
		}
	}, [shouldSetSocket]);

	const disconnectSocketListeners = () => {
		socket?.removeAllListeners();
	};

	useEffect(() => {
		const getAutoplayTimers = async (shot: Shot) => {
			const autoplayTimers = await getShotAutoplay(shot);

			if (autoplayTimers) {
				const {
					id,
					durationLeft,
				} = autoplayTimers;

				setAutoplayTimersText(`${id}: ${durationLeft}`);
			} else {
				setWatchAutoplayTimers(false);
			}
		};

		if (watchAutoplayTimers) {
			setAutoplayTimersInterval.current = setInterval(() => {
				getAutoplayTimers(currentShot);
			}, 1000);
		} else if (setAutoplayTimersInterval.current) {
			clearInterval(setAutoplayTimersInterval.current);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchAutoplayTimers]);

	const forceDeleteSession = () => {
		blpAuth.delete({
			url: `/v1/room/${roomId}/session/force`,
			host: getSyncHost(),
			options:{headers:{'id-token': blpAuth?.idToken ?? ''}},
			token: blpAuth?.accessToken ?? ''
		}).then((res) => {
			if (res.status !== 500) {
				console.log('Deleted Session');
			}
		}).catch(() => console.log('Errored Deleting Session'));
	}

	const showDelete = process.env.REACT_APP_NODE_ENV === 'local'
		|| process.env.REACT_APP_NODE_ENV === 'staging'
		|| process.env.REACT_APP_NODE_ENV === 'dev'
		|| process.env.REACT_APP_NODE_ENV === 'qa'
		|| process.env.REACT_APP_NODE_ENV === 'sandbox'

	return (<>
		<div className="section">
			<h5>Current Shot Timer</h5>
		</div>
		<div className="section">
			<button
				className="padding-small background-green text-dark border-radius-small"
				onClick={() => setWatchAutoplayTimers(
					(watching) => !watching,
				)}
			>
				{`${!watchAutoplayTimers ? 'Start' : 'Stop'} Monitoring`}
			</button>
			<pre className="code">
				{(watchAutoplayTimers && autoplayTimersText) ? (
					<>
						&#123;{'\n'}
						{'    '}{autoplayTimersText}
						{'\n'}&#125;
					</>
				) : (
					<>Not monitoring</>
				)}
			</pre>
		</div>
		<div className="section">
			<h5>Clearing</h5>
		</div>
		<div className="section reset">
			<button
				className="padding-small background-green text-dark border-radius-small"
				onClick={disconnectSocketListeners}
				title="Removes all socket listeners. Refresh to re-enable listeners."
			>
				Disconnect Socket Listeners
			</button>
		</div>
		<div className="section reset">
			<button
				className="padding-small background-green text-dark border-radius-small"
				onClick={resetLocalStorage}
				title="Clears the debug flags in local storage. Turning off this panel from settings will
				have the same effect."
			>
				Clear Debug Local Storage Objects
			</button>
		</div>
		<div className="section reset">
			<button
				className="padding-small background-green text-dark border-radius-small"
				onClick={forceDeleteSession}
				hidden={!showDelete}
				>
				Force Delete Session
			</button>
			<div>Deletes the session in Beehive's redis.</div>
			<div>Local entities will not be affected.</div>
			<div>Refresh after to re-initialize.</div>
		</div>

		{/* <div style={{ maxHeight: '200px', maxWidth: '100px' }}>
			{localLog.map((log: string, index: number) => {
				return <div key={`${log}_${index}`}>{log}</div>;
			})}
		</div> */}
		<div className="section memory-dashboard">
			<h5>Inexact Memory Usage</h5>
			<div className="memory-row">
				Memory Size Limit: ~{jsHeapSizeLimit} mb
			</div>
			<div className="memory-row">
				Memory Allocated: ~{totalJSHeapSize} mb
			</div>
			<div className="memory-row">Memory Active: ~{usedJSHeapSize} mb</div>
		</div>
		<div className="section memory-controls">
			<h5>Spam Memory Usage</h5>
			<button
				className="padding-small background-green text-dark border-radius-small"
				onClick={() => setShouldSetRedux((current) => !current)}
			>
				{!shouldSetRedux ? 'Start Redux Updates' : 'Stop Redux Updates'}
			</button>
		</div>
		<div className="section memory-controls">
			<button
				className="padding-small background-green text-dark border-radius-small"
				onClick={() => setShouldSetSocket((current) => !current)}
			>
				{!shouldSetSocket ? 'Start Socket Updates' : 'Stop Socket Updates'}
			</button>
		</div>
		<a className="blue" target="_blank" href="https://yonatankra.com/measuring-used-js-heap-size/">More on memory</a>
	</>);
}
